import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import SelectFormInput from "src/components/form/inputs/SelectFormInput/SelectFormInput";
import {
    ProjectItemsBreadcrumbs,
    TrialProjectDetailsBreadcrumbs,
    TrialProjectItemsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { ProjectSubMenu, TrialProjectSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as CollectionInstanceModalActions from "../../../actions/pages/collectionInstanceModalPage";
import * as CollectionInstanceModalPageActions from "../../../actions/pages/collectionInstanceModalPage";
import * as ProjectSettingsPageActions from "../../../actions/pages/projectSettingsPage";
import * as ToggleActions from "../../../actions/pages/togglePage";
import * as ProjectActions from "../../../actions/project";
import * as ProjectItemActions from "../../../actions/projectItems";
import * as ProjectItemTemplateActions from "../../../actions/projectItemTemplates";
import * as TrialActions from "../../../actions/trial";
import {
    InlineLoading,
    Modal,
    NotificationAlert,
} from "../../../components/common/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import CollectionInstanceComponent from "../../../containers/Collection/CollectionInstance/CollectionInstanceComponent";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { ModalSize } from "../../../enumerations/ModalSize";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { setModalTitle } from "../../../helpers/modalHelper";
import { ITmdState } from "../../../reducers/index";
import { ProjectLayout } from "../../index";

import "./ProjectItemsPage.scss";

interface ProjectItemsPageProps {
    project: Dtos.Project;
    groupedProjectItems: Dtos.ProjectItemCategory[];
    loadingProject: boolean;
    loadProjectSuccess: boolean;
    loadProjectFailure: boolean;
    loadProjectErrorCode: string;
    loadingProjectItems: boolean;
    loadProjectItemsSuccess: boolean;
    loadProjectItemsFailure: boolean;
    loadProjectItemsErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    collectionInstanceModalOpen: boolean;
    collectionInstanceId: number;
    modalTitle: string;
    modalDescription: string;
    projectItemTemplates: Dtos.ProjectItemTemplate[];
    loadingProjectItemTemplates: boolean;
    loadProjectItemTemplatesSuccess: boolean;
    loadProjectItemTemplatesFailure: boolean;
    loadProjectItemTemplatesErrorCode: string;
    savingProjectItem: boolean;
    saveProjectItemSuccess: boolean;
    saveProjectItemFailure: boolean;
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearProject: ProjectActions.IProjectClearActionCreator;
    loadProject: ProjectActions.IProjectLoadByIdActionCreator;
    clearProjectItems: ProjectItemActions.IProjectItemClearActionCreator;
    loadProjectItems: ProjectItemActions.IProjectItemsLoadByProjectIdActionCreator;
    toggleExpand: ToggleActions.IToggleExpandedStateActionCreator;
    showCollectionInstanceModal: CollectionInstanceModalActions.ICollectionInstanceModalViewStateActionCreator;
    setProject: ProjectSettingsPageActions.IProjectSettingsPageSetProjectActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearProjectItemTemplates: ProjectItemTemplateActions.IProjectItemTemplateClearActionCreator;
    loadProjectItemTemplates: ProjectItemTemplateActions.IProjectItemTemplateLoadActionCreator;
    saveProjectItem: ProjectItemActions.IProjectItemSaveActionCreator;
}

const ProjectItemsPage: FC<ProjectItemsPageProps> = ({
    project,
    groupedProjectItems,
    loadingProject,
    loadProjectSuccess,
    loadProjectFailure,
    loadProjectErrorCode,
    loadingProjectItems,
    loadProjectItemsSuccess,
    loadProjectItemsFailure,
    loadProjectItemsErrorCode,
    permissions,
    collectionInstanceModalOpen,
    collectionInstanceId,
    modalTitle,
    modalDescription,
    projectItemTemplates,
    loadingProjectItemTemplates,
    loadProjectItemTemplatesFailure,
    loadProjectItemTemplatesErrorCode,
    savingProjectItem,
    saveProjectItemSuccess,
    trial,
    loadingTrial,
    loadTrialFailure,
    loadTrialErrorCode,
    clearTrial,
    loadTrial,
    clearProject,
    loadProject,
    clearProjectItems,
    loadProjectItems,
    showCollectionInstanceModal,
    setBreadcrumbs,
    setDrawerItems,
    clearProjectItemTemplates,
    loadProjectItemTemplates,
    saveProjectItem
}) => {
    //
    // -- Params --
    const { projectId } = useParams<{ projectId: string }>();
    const projectIdNumber = parseInt(projectId, 10);

    //
    // -- Hooks --
    const location = useLocation();
    const navigate = useNavigate();

    //
    // -- States --
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [addProjectItemId, setAddProjectItemId] = useState<number | undefined>(undefined);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Variables --
    const showProjectItems =
        !loadingProject &&
        loadProjectSuccess &&
        !loadingProjectItems &&
        loadProjectItemsSuccess &&
        !creatingTree;


    // Load Project/Trial & Project Items
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            projectIdNumber &&
            baseRoute === "projects" &&
            !project &&
            !loadingProject
        ) {
            setTreeData(null);

            setBreadcrumbs(ProjectItemsBreadcrumbs(projectIdNumber));
            setDrawerItems(ProjectSubMenu(projectIdNumber, permissions));

            loadProject(projectIdNumber);
            loadProjectItems(projectIdNumber);
            loadProjectItemTemplates(null);
        }
        if (
            projectIdNumber &&
            baseRoute === "trials" &&
            !trial &&
            !loadingTrial
        ) {
            setTreeData(null);

            setDrawerItems(TrialProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(TrialProjectItemsBreadcrumbs(projectIdNumber));

            loadProject(projectIdNumber);
            loadProjectItems(projectIdNumber);
            loadProjectItemTemplates(null);
            loadTrial(projectIdNumber);
        }
    }, [
        projectIdNumber,
        permissions,
        project,
        location,
        trial,
        loadingProject,
        loadingTrial,
        setBreadcrumbs,
        setDrawerItems,
        loadProject,
        loadProjectItems,
        ProjectItemsBreadcrumbs,
        ProjectSubMenu,
        loadProjectItemTemplates,
        TrialProjectSubMenu,
        TrialProjectDetailsBreadcrumbs,
        loadTrial,
        setTreeData,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectFailure) {
            navigate(`/error/${loadProjectErrorCode}`);
        }
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadProjectItemTemplatesFailure) {
            navigate(`/error/${loadProjectItemTemplatesErrorCode}`);
        }
        if (loadProjectItemsFailure) {
            navigate(`/error/${loadProjectItemsErrorCode}`);
        }
    }, [
        loadProjectFailure,
        loadTrialFailure,
        loadProjectItemTemplatesFailure,
        loadProjectItemsFailure,
        loadProjectErrorCode,
        loadTrialErrorCode,
        loadProjectItemTemplatesErrorCode,
        loadProjectItemsErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            projectIdNumber &&
            baseRoute === "trials" &&
            trial?.displayName &&
            trial?.projectProtocolID
        ) {
            setDrawerItems(TrialProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(
                TrialProjectItemsBreadcrumbs(
                    projectIdNumber,
                    trial.displayName,
                    trial.projectProtocolID
                )
            );
        } else if (
            projectIdNumber &&
            baseRoute === "projects" &&
            project?.projectShortTitle
        ) {
            setDrawerItems(ProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(
                ProjectItemsBreadcrumbs(
                    projectIdNumber,
                    project.projectShortTitle
                )
            );
        }
    }, [
        projectIdNumber,
        project,
        permissions,
        trial,
        ProjectSubMenu,
        ProjectItemsBreadcrumbs,
        TrialProjectSubMenu,
        TrialProjectDetailsBreadcrumbs,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the project item on component unmount
    useEffect(() => {
        return () => {
            clearProject();
            clearProjectItems();
            clearProjectItemTemplates();
            clearTrial();
            setTreeData(null);
        };
    }, [
        clearProject,
        clearProjectItems,
        clearProjectItemTemplates,
        clearTrial,
        setTreeData,
    ]);

    // Create tree
    useEffect(() => {
        if (groupedProjectItems && !initalizedTreeData) {
            setCreatingTree(true);

            // If no project items return
            if (groupedProjectItems?.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (projectItems: Dtos.ProjectItem[]) => {
                let children = [];

                children =
                    projectItems
                        ?.filter((y) => y.active)
                        ?.sort((a, b) => a?.projectItemTemplateId - b?.projectItemTemplateId)
                        ?.map((y) => ({
                            id: `item-${y?.id}`,
                            data: (
                                <div className="form-row">
                                    <div className="col-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.I} />
                                    </div>
                                    <div className="col-3">
                                        {y?.projectItemTemplate?.name}
                                    </div>
                                    <div className="col">
                                        {y?.projectItemTemplate?.description}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col-3">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Description</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                groupedProjectItems
                    ?.sort((a, b) => a?.projectItemTemplateCategory?.rank - b?.projectItemTemplateCategory?.rank)
                    ?.map((x) => ({
                        id: `category-${x.categoryId}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.CLIPBOARD} />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.projectItemTemplateCategory?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {x.projectItems?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.projectItems)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        groupedProjectItems,
        initalizedTreeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    // Re initalise tree on project items update
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [groupedProjectItems, setInitalizedTreeData]);

    // Reload project & project items after saving project item
    useEffect(() => {
        if (!savingProjectItem && saveProjectItemSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Project Item saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            const baseRoute = location?.pathname?.split("/")[1];

            loadProject(projectIdNumber);
            loadProjectItems(projectIdNumber);
            if (baseRoute === "trials") {
                loadTrial(projectIdNumber);
            }
        }
    }, [
        savingProjectItem,
        saveProjectItemSuccess,
        projectIdNumber,
        location,
        loadProject,
        loadProjectItems,
        loadTrial,
    ]);

    const handleModalClose = () => {
        setIsOpen(false);
        setAddProjectItemId(undefined);
    };

    const handleModalSubmit = async () => {
        if (!addProjectItemId || !projectIdNumber) {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error occurred saving the project item</span>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
            return;
        }

        let projectItem: Dtos.ProjectItem = {
            projectId: projectIdNumber,
            projectItemTemplateId: addProjectItemId,
            active: true,
        } as Dtos.ProjectItem;
        await saveProjectItem(projectItem, null);

        setIsOpen(false);
        setAddProjectItemId(undefined);
    };

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("item") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.ProjectItemTemplatesView
                    )
                ) {
                    const projectItem = groupedProjectItems
                        .map((gpi) => gpi.projectItems.find((x) => x.id === parseInt(id?.replace("item-", ""), 10)))
                        .find((item) => item !== undefined);

                    if (projectItem) {
                        showCollectionInstanceModal(
                            true,
                            projectItem.collectionInstanceId,
                            projectItem.projectItemTemplate.name,
                            projectItem.projectItemTemplate.description
                        );
                    }
                }
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, permissions, groupedProjectItems]
    );

    const renderItemModal = useMemo(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.ProjectItemsUpdate,
            undefined,
            undefined
        );
        const formDisabled = !hasPermission || baseRoute === "trials";

        if (collectionInstanceId && collectionInstanceId > 0) {
            return (
                <Modal
                    open={collectionInstanceModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        showCollectionInstanceModal(false, null, "", "");
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h5 className="mb-0">
                                {setModalTitle(undefined, modalTitle)}
                            </h5>
                            {modalDescription ? (
                                <small>
                                    {modalDescription}
                                </small>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                showCollectionInstanceModal(false, null, "", "");
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body customscrollbar">
                        <CollectionInstanceComponent 
                            collectionInstanceId={collectionInstanceId}
                            readonly={formDisabled}
                            onClose={() => {
                                showCollectionInstanceModal(false, null, "", "");
                            }}
                            onSaved={() => {
                                loadProjectItems(projectIdNumber);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        collectionInstanceId,
        collectionInstanceModalOpen,
        permissions,
        modalTitle,
        modalDescription,
        projectIdNumber,
    ]);

    const selectOptions = useMemo(() => {
        if (!projectItemTemplates || !groupedProjectItems) return [];

        const currentIds = groupedProjectItems.flatMap((item) => item?.projectItems?.map((x) => x.projectItemTemplateId) || []);

        return projectItemTemplates
            ?.filter((x) => !currentIds.includes(x.id))
            ?.map((x) => ({ value: x.id, label: x?.name }));
    }, [projectItemTemplates, groupedProjectItems]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col mb-2"}>
                        <h1>Project Items</h1>
                    </div>
                    <div className="col-auto">
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.ProjectItemTemplatesCreate,
                            projectIdNumber,
                            null
                        ) &&
                            location?.pathname?.split("/")[1] !== "trials" && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setIsOpen(true)}
                                    >
                                        Add
                                    </button>
                                    <Modal
                                        open={isOpen}
                                        disableCloseOnOverlayClick={true}
                                        onClose={handleModalClose}
                                        size={ModalSize.Md}
                                    >
                                        <div className="modal-header">
                                            <h5 className="modal-title">
                                                Add Project Item
                                            </h5>
                                            <button
                                                type="button"
                                                className="close"
                                                aria-label="Close"
                                                onClick={handleModalClose}
                                            >
                                                <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                                            </button>
                                        </div>
                                        <div className="modal-body p-3">
                                            <div className="form-row pb-2">
                                                <div className="col-12">
                                                    <SelectFormInput
                                                        loading={loadingProjectItemTemplates}
                                                        multi={false}
                                                        options={selectOptions}
                                                        clearable={true}
                                                        backspaceRemoves={false}
                                                        name={"addProjectItemId"}
                                                        id={"addProjectItemId"}
                                                        placeholder="Project Item"
                                                        onChange={function (
                                                            value: any,
                                                            _options?: any
                                                        ): any {
                                                            setAddProjectItemId(
                                                                value !=
                                                                    undefined
                                                                    ? parseInt(value)
                                                                    : undefined
                                                            );
                                                        }}
                                                        value={addProjectItemId}
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row pt-2">
                                                <div className="col-12 d-flex justify-content-end">
                                                    <button
                                                        className="btn btn-primary mr-1"
                                                        disabled={savingProjectItem}
                                                        onClick={handleModalSubmit}
                                                    >
                                                        Submit
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-primary ml-1"
                                                        onClick={handleModalClose}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Modal>
                                </>
                            )}
                    </div>
                </div>
                {!showProjectItems ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showProjectItems ? (
                    <>
                        {treeData ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no project items
                                    </span>
                                </div>
                            </div>
                        )}
                        {renderItemModal}
                    </>
                ) : null}
            </div>
        );
    }, [
        permissions,
        projectIdNumber,
        location,
        setIsOpen,
        isOpen,
        handleModalClose,
        loadingProjectItemTemplates,
        selectOptions,
        setAddProjectItemId,
        addProjectItemId,
        handleModalSubmit,
        savingProjectItem,
        showProjectItems,
        treeData,
        handleToggle,
        toggleState,
        renderItemModal,
    ]);

    return (
        <ProjectLayout
            loading={loadingProject || loadingProjectItems || loadingTrial}
            permission={Dtos.Permission.ProjectItemsView}
        >
            {renderContent}
        </ProjectLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<ProjectItemsPageProps> => {
    let project: Dtos.Project | undefined = !(
        state.projects.formData instanceof Array
    )
        ? state.projects.formData
        : undefined;
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        project: project!,
        trial: trial!,
        loadingTrial: state.trials.loadState && state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess: state.trials.loadState && state.trials.loadState.status == RequestState.Success,
        loadTrialFailure: state.trials.loadState && state.trials.loadState.status == RequestState.Failure,
        groupedProjectItems: state.projectItems.formFilteredDataGrouped!,
        loadingProject: state.projects.loadState && state.projects.loadState.status == RequestState.Pending,
        loadProjectSuccess: state.projects.loadState && state.projects.loadState.status == RequestState.Success,
        loadProjectFailure: state.projects.loadState && state.projects.loadState.status == RequestState.Failure,
        loadingProjectItems: state.projectItems.loadState && state.projectItems.loadState.status == RequestState.Pending,
        loadProjectItemsSuccess: state.projectItems.loadState && state.projectItems.loadState.status == RequestState.Success,
        loadProjectItemsFailure: state.projectItems.loadState && state.projectItems.loadState.status == RequestState.Failure,
        user: state.user.data,
        permissions: permissions,
        collectionInstanceModalOpen: state.collectionInstanceModalPage.editModalOpen,
        collectionInstanceId: state.collectionInstanceModalPage.collectionInstanceId,
        modalTitle: state.collectionInstanceModalPage.name,
        modalDescription: state.collectionInstanceModalPage.description,
        projectItemTemplates: state.projectItemTemplates.formFilteredData!,
        loadingProjectItemTemplates: state.projectItemTemplates.loadState && state.projectItemTemplates.loadState.status == RequestState.Pending,
        loadProjectItemTemplatesSuccess: state.projectItemTemplates.loadState && state.projectItemTemplates.loadState.status == RequestState.Success,
        loadProjectItemTemplatesFailure: state.projectItemTemplates.loadState && state.projectItemTemplates.loadState.status == RequestState.Failure,
        savingProjectItem: state.projectItems.saveState && state.projectItems.saveState.status == RequestState.Pending,
        saveProjectItemSuccess: state.projectItems.saveState && state.projectItems.saveState.status == RequestState.Success,
        saveProjectItemFailure: state.projectItems.saveState && state.projectItems.saveState.status == RequestState.Failure,
    };
};

const mapDispatchToProps = (dispatch): Partial<ProjectItemsPageProps> => {
    return {
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        loadProject: bindActionCreators(ProjectActions.LoadProjectById, dispatch),
        clearProject: bindActionCreators(ProjectActions.Clear, dispatch),
        loadProjectItems: bindActionCreators(ProjectItemActions.LoadProjectItemsByProjectId, dispatch),
        clearProjectItems: bindActionCreators(ProjectItemActions.Clear, dispatch),
        showCollectionInstanceModal: bindActionCreators(CollectionInstanceModalPageActions.setModalViewState, dispatch),
        setProject: bindActionCreators(ProjectSettingsPageActions.SetProject, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadProjectItemTemplates: bindActionCreators(ProjectItemTemplateActions.LoadProjectItemTemplate, dispatch),
        clearProjectItemTemplates: bindActionCreators(ProjectItemTemplateActions.Clear, dispatch),
        saveProjectItem: bindActionCreators(ProjectItemActions.SaveProjectItem, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectItemsPage);
